import { useNavigate } from 'react-router-dom';

import { agentRoleLabel, useGetAgentsQuery } from '../../store/agents-api.js';
import { messageOf } from '../../store/error-message.js';
import { Spinner } from '../../ui/BrandLogo.js';
import { initials } from '../../ui/initials.js';
import { when } from '../../ui/when.js';
import styles from './AgentsPage.module.css';

/**
 * Agent configuration (S10).
 *
 * ## Why this list is short
 *
 * The platform project holds many agents, shared with other demos. The console
 * shows the ones it uses: the conversation agent behind each line, and the
 * analyzer that reads the calls afterwards. Offering the rest would invite a
 * change with no visible effect here and an unknown effect elsewhere.
 *
 * ## Why the editor is a route and not a panel
 *
 * Instructions run to five thousand characters. The interaction drawer works
 * because a summary and a transcript are read together; a prompt is *written*,
 * with the line numbers and the width of an editor. Making it a route rather than
 * state held here is what lets the sidebar link lead back out of it.
 */
export function AgentsPage(): JSX.Element {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useGetAgentsQuery();

  const agents = data?.items ?? [];

  return (
    <section className={styles.view}>
      {isLoading && (
        <div className={styles.state}>
          <span className={styles.loading}>
            <Spinner />
            Loading agents…
          </span>
        </div>
      )}

      {isError && (
        <div className={styles.state}>
          <p>{messageOf(error)}</p>
          <button type="button" className={styles.retry} onClick={() => void refetch()}>
            Try again
          </button>
        </div>
      )}

      {!isLoading &&
        !isError &&
        agents.map((agent) => (
          <div
            key={agent.id}
            className={styles.card}
            role="button"
            tabIndex={0}
            aria-label={`Configure ${agent.presetName ?? agent.name}`}
            onClick={() => void navigate(`/agents/${agent.id}`)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                void navigate(`/agents/${agent.id}`);
              }
            }}
          >
            <div className={styles.av} aria-hidden="true">
              {initials(agent.presetName ?? agent.name)}
            </div>

            <div className={styles.main}>
              <div className={styles.titleRow}>
                <b>{agent.presetName ?? agent.name}</b>
                <span className={styles.role}>{agentRoleLabel(agent)}</span>
              </div>

              {agent.description !== null && <p className={styles.desc}>{agent.description}</p>}

              <div className={styles.meta}>
                {/* The platform's own timestamp, not ours: what an operator wants
                    to know is when the agent last changed, wherever it was
                    changed from. */}
                <span>
                  Updated {agent.sirmaUpdatedAt === null ? 'never' : when(agent.sirmaUpdatedAt)}
                  {agent.lastUpdatedBy !== null && ` by ${agent.lastUpdatedBy}`}
                </span>
                {agent.role === 'CONVERSATION' && (
                  <>
                    <span className={styles.dot} aria-hidden="true">
                      ·
                    </span>
                    <span>Speaks as {agent.name}</span>
                    {agent.lines.length > 0 && (
                      <>
                        <span className={styles.dot} aria-hidden="true">
                          ·
                        </span>
                        <span>
                          Answers {agent.lines.map((line) => line.label ?? line.number).join(', ')}
                        </span>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        ))}

      {!isLoading && !isError && agents.length === 0 && (
        <div className={styles.empty}>
          <h3>No agents configured</h3>
          {/* Deliberately specific: an empty list here almost always means a
              missing id or an unreachable platform, not an empty project. */}
          <p>
            The console shows the agent behind each line and the configured analyzer. If this is
            empty, the lines have not synced, the analyzer id is unset, or the platform could not be
            reached.
          </p>
        </div>
      )}
    </section>
  );
}
